import React, { useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    FormGroup,
    FormControl,
} from "react-bootstrap";
import { Form, redirect, useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { validateResetToken } from "../../lib/api";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IPasswordResetParams } from "../../types/users";
import { displayFormAlert } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { authActions } from "../../features/auth/authSlice";
import { alertActions } from "../../features/alert/alertSlice";
import { store } from "../..";

export const loader = async ({ request }: any) => {
    const searchParams = new URL(request.url).searchParams;
    const token = searchParams.get("token");
    const userId = searchParams.get("userId");
    const dispatch = store.dispatch;

    try {
        if (token === null || userId === null) {
            return redirect("/login");
        } else {
            // if this returns, the token is valid. otherwise it will throw an exception
            await validateResetToken(token, userId);
        }
    } catch (err) {
        dispatch(
            alertActions.error({
                title: "Error encountered",
                text: err as string,
                persist: true,
                dismissible: true,
            }),
        );
        return redirect("/login");
    }

    return null;
};

const ResetPassword = () => {
    const { loading, user, loggedIn } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedIn) {
            navigate("/profile");
        }
    }, [navigate, user, loggedIn]);

    const pwResetFormSchema = yup.object().shape({
        userId: yup.number().required(),
        password: yup
            .string()
            .required("Password is required")
            .min(8, "Password length should be at least 8 characters"),
        confirmPassword: yup
            .string()
            .required("Please confirm your password")
            .min(8, "Password length should be at least 8 characters")
            .oneOf([yup.ref("password")], "Passwords do not match"),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IPasswordResetParams>({
        mode: "onChange",
        resolver: yupResolver(pwResetFormSchema),
    });

    const onSubmit: SubmitHandler<IPasswordResetParams> = async ({
        userId,
        password,
        confirmPassword,
    }) => {
        const resetPasswordResponse = await dispatch(
            authActions.resetPassword({
                userId,
                password,
                confirmPassword,
            }),
        );

        if (resetPasswordResponse.type === "auth/resetPassword/fulfilled") {
            if (typeof resetPasswordResponse.payload !== "string") {
                const loginResponse = await dispatch(
                    authActions.login({
                        email: resetPasswordResponse.payload!.email,
                        password,
                        remember: false,
                    }),
                );

                if (loginResponse.type !== "auth/login/fulfilled") {
                    dispatch(
                        alertActions.warning({
                            text: "Your password has been succesfully reset but there was a problem signing you in. Please try to sign in manually with your new password and if it continues to fail, contact the site administrators",
                            title: "Success!",
                        }),
                    );
                } else {
                    dispatch(
                        alertActions.success({
                            text: "Password reset successfully",
                            title: "Success!",
                            persist: true,
                            dismissible: true,
                        }),
                    );
                    navigate("/profile");
                }
            }
        }
    };

    return (
        <div data-testid="resetPasswordContent">
            <Container>
                <Row className="justify-content-center">
                    <Col lg="6" md="9">
                        <Card className="o-hidden border-0 shadow-lg my-5">
                            <Card.Body className="p-0">
                                <Row>
                                    <Col className="p-5">
                                        <div className="text-center">
                                            <h2 className="text-gray-900 mb-4">
                                                Reset Password
                                            </h2>
                                        </div>
                                        <Form
                                            onSubmit={handleSubmit(onSubmit)}
                                            method="post"
                                            replace
                                            className="user"
                                            id="resetPassword"
                                        >
                                            <FormControl
                                                type="hidden"
                                                value={
                                                    searchParams.get(
                                                        "userId",
                                                    ) as string
                                                }
                                                {...register("userId")}
                                            />
                                            <FormGroup className="form-group">
                                                <FormControl
                                                    type="password"
                                                    placeholder="New password..."
                                                    className="form-control form-control-user"
                                                    {...register("password")}
                                                />
                                                {errors.password &&
                                                    displayFormAlert({
                                                        level: "danger",
                                                        text: errors.password
                                                            .message as string,
                                                    })}
                                            </FormGroup>
                                            <FormGroup className="form-group">
                                                <FormControl
                                                    type="password"
                                                    placeholder="Confirm password..."
                                                    className="form-control form-control-user"
                                                    {...register(
                                                        "confirmPassword",
                                                    )}
                                                />
                                                {errors.confirmPassword &&
                                                    displayFormAlert({
                                                        level: "danger",
                                                        text: errors
                                                            .confirmPassword
                                                            .message as string,
                                                    })}
                                            </FormGroup>
                                            <Button
                                                disabled={loading}
                                                variant="primary"
                                                type="submit"
                                            >
                                                {loading
                                                    ? "Resetting password..."
                                                    : "Reset password"}
                                            </Button>
                                        </Form>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ResetPassword;
